import { CSSProperties, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import Collapse from '@/components/shared/Collapse';
import CollapseArrowSVG from '@/svg/collapse-arrow.svg';
import Link from '@/components/shared/Link';
import Image from 'next/image';
import Button from '@/components/shared/Button';
import { useTypograf } from '@/hooks/use-typograf';
import { CategoryId } from '@/api/categories';
import { useMediaQuery } from 'usehooks-ts';
import { useMediaQueryDeviceState } from '@/atoms/media-query-device';

type collapsesKeys = 'characteristic' | 'brand' | `accordion-${number}`;

type CollapsedSectionProps = {
    title: string;
    link?: ReactNode;
    collapsed: boolean;
    children: ReactNode;
};

const CollapsedSectionWrapper = ({ title, collapsed, children }: CollapsedSectionProps) => {
    const [mediaQueryDevice] = useMediaQueryDeviceState();
    const tpTitle = useTypograf(title);

    if (mediaQueryDevice !== 'desktop') {
        return (
            <Collapse collapsed={collapsed}>
                <Collapse.Toggler className="product-collapse-section-title h2">{tpTitle}</Collapse.Toggler>
                <Collapse.Content>
                    <div className="product-collapse-section__inner">{children}</div>
                </Collapse.Content>
            </Collapse>
        );
    }
    return children;
};

interface Props extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
    brand: {
        id: number;
        name: string;
        description: string;
    };
    categoryId: CategoryId;
    reference: string | null;
    diameter: string | null;
    isStandardForm: boolean;
    accessories: string | null;
    color: string | null;
    type: string | null;
    params: string | null;
    insert: string | null;
    collection: string | null;
    mechanism: string | null;
    bracelet: string | null;
    powerReserve: string | null;
    dialColor: string | null;
    materials: Array<{ id: number; name: string }>;
    genders: Array<{ id: number; name: string }>;
    options: Array<{ id: number; name: string }>;
    additionalCharacteristics: Array<{ name: string; value: string }>;
    accordions: Array<{ name: string; text: string }>;
}

const ProductInfoSection = ({
    brand,
    categoryId,
    reference,
    diameter,
    isStandardForm,
    accessories,
    color,
    type,
    params,
    insert,
    collection,
    mechanism,
    bracelet,
    powerReserve,
    dialColor,
    materials,
    genders,
    options,
    additionalCharacteristics,
    accordions,
    ...props
}: Props) => {
    const productRightRef = useRef<HTMLDivElement>(null);
    const [productRightWidth, setProductRightWidth] = useState(productRightRef.current?.offsetWidth);

    const [activeCollapseIndex, setActiveCollapseIndex] = useState<number | null>(null);
    const [isAnyExpanded, setIsAnyExpanded] = useState(true);

    const tpBrandName = useTypograf(brand.name ?? '');
    const tpBrandDescription = useTypograf(brand.description ?? '');
    const tpReference = useTypograf(reference ?? '');
    const tpAccessories = useTypograf(accessories ?? '');
    const tpColor = useTypograf(color ?? '');
    const tpType = useTypograf(type ?? '');
    const tpParams = useTypograf(params ?? '');
    const tpInsert = useTypograf(insert ?? '');
    const tpCollection = useTypograf(collection ?? '');
    const tpMechanism = useTypograf(mechanism ?? '');
    const tpBracelet = useTypograf(bracelet ?? '');
    const tpPowerReserve = useTypograf(powerReserve ?? '');
    const tpDialColor = useTypograf(dialColor ?? '');

    const isTablet = useMediaQuery('(max-width: 1199px)');

    const mergedAccordions = [
        ...accordions,
        {
            name: 'Оплата',
            text: 'Мы принимаем оплату практически всеми удобными для клиента способами.',
        },
        {
            name: 'Гарантия',
            text: `
      <div class="step-text">На подержанные часы и украшения действует гарантия не менее 12 месяцев.</div>
      <div class="step-text">Новые изделия сопровождаются международной гарантией сроком от 2 лет — в зависимости от политики конкретного бренда.</div>
      <div class="step-text">Каждое изделие проходит очную экспертизу на подлинность и полное соответствие стандартам качества.</div>
      <div class="step-text">Бутик ROTORMINE также предоставляет 1 год дополнительной гарантии на случай заводского брака.</div>
      <div class="step-text">Вы можете быть уверены: если изделие окажется неисправным или не будет соответствовать заявленным характеристикам, мы предложим замену, профессиональный ремонт или полный возврат средств, включая все понесённые расходы.</div>
    `,
        },
        {
            name: 'Доставка',
            text: `
      <div class="step-text">Мы организуем доставку в день оформления заказа.</div>
      <div class="step-text">Также доступна услуга срочной доставки — в течение 1–2 часов.</div>
    `,
        },
    ];

    const collapsesMap: Record<collapsesKeys, number> = {
        characteristic: 0,
        brand: 1,
        ...mergedAccordions.reduce(
            (acc, _, i) => ({
                ...acc,
                [`accordion-${i}`]: i + 2,
            }),
            {},
        ),
    };

    const onToggleHandler = (key: collapsesKeys) => {
        if (activeCollapseIndex === collapsesMap[key]) {
            setIsAnyExpanded(false);
        } else {
            isAnyExpanded ? setIsAnyExpanded(true) : setTimeout(() => setIsAnyExpanded(true), 500);
            setActiveCollapseIndex(collapsesMap[key]);
        }
    };

    useEffect(() => {
        const onResize = () => {
            isTablet && setProductRightWidth(productRightRef.current?.offsetWidth);
        };

        setTimeout(() => {
            onResize();
        }, 0);

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [isTablet]);

    return (
        <div
            ref={productRightRef}
            id="product-right"
            className="product-right"
            style={
                {
                    '--product-right-width': productRightWidth ? `${productRightWidth}px` : 'auto',
                    '--collapse-delay': `${isAnyExpanded ? 0.5 : 0}s`,
                } as CSSProperties
            }
            {...props}
        >
            <CollapsedSectionWrapper title="Подробнее" collapsed={true}>
                <Collapse
                    className="product-collapse"
                    collapsed={
                        !activeCollapseIndex || activeCollapseIndex === collapsesMap['characteristic'] ? false : true
                    }
                >
                    <Collapse.Toggler
                        className="product-collapse-toggler"
                        onToggle={() => onToggleHandler('characteristic')}
                    >
                        <div className="product-collapse-options text-xs">характеристики</div>
                        <div className="product-collapse-toggler__icon">
                            <CollapseArrowSVG />
                        </div>
                    </Collapse.Toggler>
                    <Collapse.Content>
                        <div className="product-collapse-content">
                            <ul className="product-characteristic list-unstyled">
                                {brand.name !== '' && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">
                                            название бренда
                                        </div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            <Button
                                                tag={Link}
                                                icon={<Image src="/img/back.svg" alt="" unoptimized fill />}
                                                href={`/catalog?category=${categoryId}&brands[]=${brand.id}&sort=published_at&order=desc`}
                                                className="button-arrow-reverse"
                                                geometryVariant="arrow"
                                            >
                                                {tpBrandName}
                                            </Button>
                                        </div>
                                    </li>
                                )}
                                {reference && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Ref</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {tpReference}
                                        </div>
                                    </li>
                                )}
                                {diameter && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">диаметр</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {isStandardForm ? `${diameter} мм` : diameter}
                                        </div>
                                    </li>
                                )}
                                {accessories && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">
                                            Цвет Фурнитуры
                                        </div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {tpAccessories}
                                        </div>
                                    </li>
                                )}
                                {color && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Цвет</div>
                                        <div className="product-characteristic-item__value m-text-xs">{tpColor}</div>
                                    </li>
                                )}
                                {type && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Тип</div>
                                        <div className="product-characteristic-item__value m-text-xs">{tpType}</div>
                                    </li>
                                )}
                                {params && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Параметры</div>
                                        <div className="product-characteristic-item__value m-text-xs">{tpParams}</div>
                                    </li>
                                )}
                                {insert && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Вставка</div>
                                        <div className="product-characteristic-item__value m-text-xs">{tpInsert}</div>
                                    </li>
                                )}
                                {collection && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Коллекция</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {tpCollection}
                                        </div>
                                    </li>
                                )}
                                {mechanism && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Механизм</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {tpMechanism}
                                        </div>
                                    </li>
                                )}
                                {bracelet && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Браслет</div>
                                        <div className="product-characteristic-item__value m-text-xs">{tpBracelet}</div>
                                    </li>
                                )}
                                {powerReserve && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Запас хода</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {tpPowerReserve}
                                        </div>
                                    </li>
                                )}
                                {dialColor && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">
                                            Цвет циферблата
                                        </div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {tpDialColor}
                                        </div>
                                    </li>
                                )}
                                {materials && materials.length > 0 && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Материал</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {materials.map((item) => item.name).join(', ')}
                                        </div>
                                    </li>
                                )}
                                {genders && genders.length > 0 && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Гендеры</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {genders.map((item) => item.name).join(', ')}
                                        </div>
                                    </li>
                                )}
                                {options && options.length > 0 && (
                                    <li className="product-characteristic-item">
                                        <div className="product-characteristic-item__name m-text-xs">Опции</div>
                                        <div className="product-characteristic-item__value m-text-xs">
                                            {options.map((item) => item.name).join(', ')}
                                        </div>
                                    </li>
                                )}
                                {additionalCharacteristics &&
                                    additionalCharacteristics.map((item, i) => {
                                        return (
                                            <li className="product-characteristic-item" key={i}>
                                                <div className="product-characteristic-item__name m-text-xs">
                                                    {item.name}
                                                </div>
                                                <div className="product-characteristic-item__value m-text-xs">
                                                    {item.value}
                                                </div>
                                            </li>
                                        );
                                    })}
                            </ul>
                        </div>
                    </Collapse.Content>
                </Collapse>
                {tpBrandDescription && (
                    <Collapse
                        className="product-collapse"
                        collapsed={activeCollapseIndex === collapsesMap['brand'] ? false : true}
                    >
                        <Collapse.Toggler
                            className="product-collapse-toggler"
                            onToggle={() => onToggleHandler('brand')}
                        >
                            <div className="text-xs">О бренде</div>
                            <div className="product-collapse-toggler__icon">
                                <CollapseArrowSVG />
                            </div>
                        </Collapse.Toggler>
                        <Collapse.Content>
                            <div className="product-collapse-content">
                                <div
                                    className="product-collapse-text text-s"
                                    dangerouslySetInnerHTML={{ __html: tpBrandDescription }}
                                />
                            </div>
                        </Collapse.Content>
                    </Collapse>
                )}
                {mergedAccordions.map((item, i) => {
                    const accordionKey = `accordion-${i}` as collapsesKeys;

                    return (
                        item.text && (
                            <Collapse
                                className="product-collapse"
                                collapsed={activeCollapseIndex === collapsesMap[accordionKey] ? false : true}
                                key={i}
                            >
                                <Collapse.Toggler
                                    className="product-collapse-toggler"
                                    onToggle={() => onToggleHandler(accordionKey)}
                                >
                                    <div className="text-xs">{item.name}</div>
                                    <div className="product-collapse-toggler__icon">
                                        <CollapseArrowSVG />
                                    </div>
                                </Collapse.Toggler>
                                <Collapse.Content>
                                    <div className="product-collapse-content">
                                        <div
                                            className="product-collapse-text text-s"
                                            dangerouslySetInnerHTML={{ __html: item.text }}
                                        />
                                    </div>
                                </Collapse.Content>
                            </Collapse>
                        )
                    );
                })}
            </CollapsedSectionWrapper>
        </div>
    );
};

export default ProductInfoSection;
